import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CheckCircle, Pencil, XCircle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api/bd`
  : "/api/bd";

const EMPTY_PANELS = [];

function panelKey(panel, index) {
  return String(panel?.panel_index ?? index + 1);
}

function descriptionOf(panel) {
  return panel?.description || "";
}

function changedPanels(originalPanels, proposedPanels) {
  const rows = proposedPanels.map((panel, index) => {
    const original =
      originalPanels.find((p) => p?.panel_index === panel?.panel_index) ||
      originalPanels[index] ||
      {};
    return { panel, original, index };
  });
  const changed = rows.filter(
    ({ panel, original }) => descriptionOf(panel) !== descriptionOf(original)
  );
  return changed.length ? changed : rows;
}

export default function ModerationReformulationCard({
  token,
  clientId,
  chapterIndex,
  proposedReformulation,
  onRefresh,
  onRegen,
}) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const proposedPanels = proposedReformulation?.scene_spec || EMPTY_PANELS;
  const originalPanels = proposedReformulation?.original_scene_spec || EMPTY_PANELS;
  const rows = useMemo(
    () => changedPanels(originalPanels, proposedPanels),
    [originalPanels, proposedPanels],
  );
  const [draftDescriptions, setDraftDescriptions] = useState({});

  useEffect(() => {
    const next = {};
    proposedPanels.forEach((panel, index) => {
      next[panelKey(panel, index)] = descriptionOf(panel);
    });
    setDraftDescriptions(next);
  }, [proposedPanels]);

  if (!proposedReformulation) return null;

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const chapterKey = `${clientId}-${chapterIndex}`;

  const post = async (path, body = {}) => axios.post(
    `${API}/chapters/${chapterKey}/moderation/${path}`,
    body,
    { headers },
  );

  const approve = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await post("approve");
      toast.success("Reformulation validée, régénération lancée.");
      if (onRegen) onRegen();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const submitEdit = async () => {
    if (busy) return;
    const edited = proposedPanels.map((panel, index) => ({
      ...panel,
      description: draftDescriptions[panelKey(panel, index)] || "",
    }));
    setBusy(true);
    try {
      await post("edit", { scene_spec: edited });
      toast.success("Reformulation modifiée, régénération lancée.");
      setEditing(false);
      if (onRegen) onRegen();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await post("reject");
      toast.success("Reformulation annulée.");
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-amber-300 bg-amber-50 rounded p-3 text-sm space-y-3">
      <div>
        <p className="font-semibold text-amber-950">
          Scène bloquée par la modération
        </p>
        <p className="text-xs text-amber-800">
          Reformulation proposée pour le chapitre {chapterIndex}
        </p>
      </div>

      <div className="space-y-3">
        {rows.map(({ panel, original, index }) => {
          const key = panelKey(panel, index);
          return (
            <div key={key} className="border border-amber-200 rounded bg-white p-2">
              <p className="text-xs font-semibold text-gray-600 mb-2">
                Case {panel?.panel_index ?? index + 1}
              </p>
              <div className="grid grid-cols-1 gap-2">
                <div>
                  <p className="text-xs font-semibold text-gray-500">
                    Description originale
                  </p>
                  <p className="text-gray-800 whitespace-pre-wrap">
                    {descriptionOf(original) || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500">
                    Reformulation proposée
                  </p>
                  {editing ? (
                    <textarea
                      value={draftDescriptions[key] || ""}
                      onChange={(e) => setDraftDescriptions((prev) => ({
                        ...prev,
                        [key]: e.target.value,
                      }))}
                      rows={3}
                      className="w-full border rounded p-2 text-sm"
                    />
                  ) : (
                    <p className="text-gray-900 whitespace-pre-wrap">
                      {descriptionOf(panel) || "—"}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={approve}
          disabled={busy}
          className="inline-flex items-center gap-1 bg-green-600 text-white text-xs px-3 py-1.5 rounded disabled:opacity-50 hover:bg-green-700"
        >
          <CheckCircle size={14} />
          Valider et régénérer
        </button>
        <button
          type="button"
          onClick={editing ? submitEdit : () => setEditing(true)}
          disabled={busy}
          className="inline-flex items-center gap-1 bg-blue-600 text-white text-xs px-3 py-1.5 rounded disabled:opacity-50 hover:bg-blue-700"
        >
          <Pencil size={14} />
          {editing ? "Envoyer modification" : "Modifier"}
        </button>
        {editing && (
          <button
            type="button"
            onClick={() => setEditing(false)}
            disabled={busy}
            className="border border-gray-300 text-gray-700 text-xs px-3 py-1.5 rounded disabled:opacity-50 hover:bg-gray-100"
          >
            Fermer l'édition
          </button>
        )}
        <button
          type="button"
          onClick={reject}
          disabled={busy}
          className="inline-flex items-center gap-1 bg-gray-700 text-white text-xs px-3 py-1.5 rounded disabled:opacity-50 hover:bg-gray-800"
        >
          <XCircle size={14} />
          Annuler
        </button>
      </div>
    </div>
  );
}
